import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
} from '../components/ui/Primitives';
import { useTaillePage } from '../lib/useTaillePage';
import {
  useMarquerLues,
  useNotifications,
  type CanalNotification,
  type LigneNotification,
} from '../lib/useNotifications';
import { ErreurApi } from '../lib/api';
import { adresseQuestion } from '../lib/useLecteur';
import { BandeauExpedition } from '../components/notifications/BandeauExpedition';
import { useAuth } from '../auth/useAuth';

function horodatage(valeur: string): string {
  return new Date(valeur).toLocaleString('fr-FR', {
    timeZone: 'Indian/Reunion',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

/**
 * Écran « Notifications » (CDC §11).
 *
 * Historique de ce que le compte connecté a reçu — et de lui seul : ces
 * messages nomment des apprenants et des motifs d'alerte, deux personnes d'une
 * même antenne n'ont pas à lire le courrier de l'autre.
 */
export function NotificationsPage(): JSX.Element {
  const [canal, setCanal] = useState<CanalNotification | ''>('');
  const [nonLues, setNonLues] = useState(false);
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  useEffect(() => {
    setPage(1);
  }, [canal, nonLues, taillePage]);

  const { data, isLoading, error } = useNotifications({
    ...(canal ? { canal } : {}),
    ...(nonLues ? { nonLues: true } : {}),
  });
  const marquer = useMarquerLues();

  const compteurs = data?.compteurs;
  const expedition = data?.expedition;
  const notificationsTotales = useMemo(() => data?.notifications ?? [], [data?.notifications]);
  const total = notificationsTotales.length;
  const pages = Math.max(1, Math.ceil(total / taillePage));
  const notificationsAffichees = useMemo(() => {
    const debut = (page - 1) * taillePage;
    return notificationsTotales.slice(debut, debut + taillePage);
  }, [notificationsTotales, page, taillePage]);

  return (
    <div className="mx-auto max-w-[1100px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Notifications"
        sousTitre="Historique des alertes et informations qui vous ont été adressées"
        actions={
          compteurs && compteurs.nonLues > 0 ? (
            <BoutonSecondaire onClick={() => marquer.mutate(undefined)} disabled={marquer.isPending}>
              {marquer.isPending
                ? 'Marquage…'
                : `Tout marquer comme lu (${compteurs.nonLues})`}
            </BoutonSecondaire>
          ) : undefined
        }
      />

      <BandeauExpedition
        etat={expedition}
        contexte="Cet écran conserve l’historique de tous les messages, expédiés ou seulement tracés."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Non lues"
          valeur={compteurs?.nonLues ?? '—'}
          detail="en attente de lecture"
          ton="warm"
        />
        <Compteur
          libelle="Reçues"
          valeur={compteurs?.total ?? '—'}
          detail="depuis l’ouverture du compte"
          ton="navy"
        />
        <Compteur
          libelle="Par mail"
          valeur={compteurs?.mails ?? '—'}
          detail="canal courriel"
          ton="blue"
        />
        <Compteur
          libelle="Par SMS"
          valeur={compteurs?.sms ?? '—'}
          detail="canal téléphone"
          ton="mint"
        />
      </div>

      <Carte
        titre="Historique"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="Canal"
              value={canal}
              onChange={(evenement) => setCanal(evenement.target.value as CanalNotification | '')}
              className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs"
            >
              <option value="">Tous les canaux</option>
              <option value="IN_APP">Application</option>
              <option value="EMAIL">Mail</option>
              <option value="SMS">SMS</option>
              <option value="PUSH">Push</option>
            </select>
            <button
              type="button"
              onClick={() => setNonLues((valeur) => !valeur)}
              aria-pressed={nonLues}
              className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition ${
                nonLues
                  ? 'border-codialis-mint bg-codialis-sand text-codialis-navy'
                  : 'border-gray-200 bg-gray-50 text-codialis-gray'
              }`}
            >
              Non lues
            </button>
          </div>
        }
        sansBordure
      >
        {error && (
          <div className="p-4 sm:p-6">
            <MessageErreur
              message={
                error instanceof ErreurApi
                  ? error.message
                  : 'L’historique n’a pas pu être chargé.'
              }
            />
          </div>
        )}
        {isLoading && <Chargement libelle="Chargement de l’historique…" />}

        <div className="divide-y divide-gray-100">
          {!isLoading && !error && (data?.notifications ?? []).length === 0 && (
            <p className="px-4 sm:px-6 py-10 text-center text-sm text-codialis-gray">
              {nonLues || canal
                ? 'Aucune notification ne correspond aux filtres actifs'
                : 'Aucune notification reçue'}
            </p>
          )}
          {notificationsAffichees.map((notification) => (
            <LigneRecue
              key={notification.id}
              notification={notification}
              enCours={marquer.isPending}
              onMarquerLue={() => marquer.mutate([notification.id])}
            />
          ))}
        </div>

        {!isLoading && !error && total > 0 && (
          <Pagination
            page={page}
            pages={pages}
            total={total}
            affiches={notificationsAffichees.length}
            taillePage={taillePage}
            onPage={setPage}
            onChangerTaille={setTaillePage}
            uniteLabel="notification"
          />
        )}
      </Carte>
    </div>
  );
}

function LigneRecue({
  notification,
  enCours,
  onMarquerLue,
}: {
  notification: LigneNotification;
  enCours: boolean;
  onMarquerLue: () => void;
}): JSX.Element {
  const { peutAcceder } = useAuth();
  // Le pôle facturation ouvre le dossier en vue réduite, par « Financeurs & OPCO » (TF-404).
  const peutOuvrirDossier = peutAcceder('adm-dossier') || peutAcceder('financing');
  return (
    <div className={`flex items-start gap-3 px-4 sm:px-6 py-4 ${notification.lu ? '' : 'bg-codialis-sand/40'}`}>
      <span
        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
          notification.lu ? 'bg-gray-300' : 'bg-codialis-sky'
        }`}
        role="img"
        aria-label={notification.lu ? 'lue' : 'non lue'}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`text-sm ${
              notification.lu
                ? 'text-codialis-navy'
                : 'font-semibold text-codialis-navy'
            }`}
          >
            {notification.titre}
          </span>
          {notification.canaux.map((canal) => (
            <Pastille key={canal.canal} texte={canal.libelle} ton={canal.ton} />
          ))}
        </div>
        {/* Un récapitulatif liste ses points ligne à ligne, comme le mail parti. */}
        <p className="mt-1 whitespace-pre-line break-words text-xs text-codialis-gray">{notification.corps}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-codialis-gray">
          <span>{horodatage(notification.envoyeeLe)}</span>
          {notification.regle && <span>· {notification.regle}</span>}
          {/* Le dossier ne s'ouvre qu'à qui y a accès : un formateur lit son
              apprenant depuis son tableau de bord, pas depuis le dossier. */}
          {notification.apprenantId &&
            (peutOuvrirDossier ? (
              <Link
                to={`/adm-dossier/${notification.apprenantId}`}
                className="font-semibold text-codialis-sky hover:underline"
              >
                · {notification.apprenant}
              </Link>
            ) : (
              <span>· {notification.apprenant}</span>
            ))}
          {notification.factureId && peutAcceder('invoicing') && (
            <Link
              to={`/invoicing?facture=${notification.factureId}`}
              className="font-semibold text-codialis-sky hover:underline"
            >
              · Voir la facture
            </Link>
          )}
          {notification.reponseId && peutAcceder('satisfaction') && (
            <Link
              to={`/satisfaction?reponse=${encodeURIComponent(notification.reponseId)}`}
              className="font-semibold text-codialis-sky hover:underline"
            >
              · Voir la réponse
            </Link>
          )}
          {notification.discussion && peutAcceder('player') && (
            <Link
              to={adresseQuestion(notification.discussion.leconId, notification.discussion.questionId)}
              className="font-semibold text-codialis-sky hover:underline"
            >
              · Voir la question
            </Link>
          )}
          {notification.formateurIaId && peutAcceder('ai-monitoring') && (
            <Link
              to={`/ai-monitoring?formateur=${encodeURIComponent(notification.formateurIaId)}`}
              className="font-semibold text-codialis-sky hover:underline"
            >
              · Voir l’usage du formateur
            </Link>
          )}
        </div>
      </div>
      {!notification.lu && (
        <button
          type="button"
          onClick={onMarquerLue}
          disabled={enCours}
          className="shrink-0 text-xs font-semibold text-codialis-sky hover:underline disabled:opacity-50"
        >
          Marquer lue
        </button>
      )}
    </div>
  );
}
